import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from "react-redux"
import { toast } from 'react-toastify';
import ProfileCombo from '../../sharedComponents/ProfileCombo';
import ProfileDetail from './ProfileDetail';
import ProfileOrders from './ProfileOrders';
import CustomerCaseHistory from '../caseTicket/CustomerCaseHistory';

import { getCustomerProfileBySmile, getCustomerProfiles, selectCustomerProfile, selectCustomerProfiles } from '../../redux/features/customerProfileSlice';
import { setShop } from '../../redux/features/shopSlice';
import useCustomerProfile from './useCustomerProfile';
import { useAuth } from '../../context/AuthContext';


const CustomerProfile = () => {
  const dispatch = useDispatch();
  const profiles = useSelector(selectCustomerProfiles)
  const profile = useSelector(selectCustomerProfile)
  const [smileNameState, setSmileNameState] = useState('')
  const [ordersPage, setOrdersPage] = useState(1);
  const [selectedShipDateId, setSelectedShipDateId] = useState('all');
  const shopName = useSelector((state) => state.shop.name);
  const { authState } = useAuth();
  const isSuperuser = authState?.user?.role === 'superuser';
  const { removeProfileFromBlacklist, addProfileToBlacklist } = useCustomerProfile();

  const fetchProfileWithOrders = (page, shipDateId = selectedShipDateId) => {
    // Use smileNameState if available, otherwise fall back to profile.smileName
    const smileName = smileNameState || profile?.smileName;
    
    // Only fetch if we have a smileName
    if (!smileName) {
      return;
    }
    
    dispatch(getCustomerProfileBySmile({ 
      smileName: smileName, 
      includeOrders: true, 
      shop: shopName, 
      page: page,
      shipDateId: shipDateId 
    }));
  };

  const handleSelectedChange = ((smileName)=>{
    setOrdersPage(1); // Reset to first page when selecting new profile
    setSelectedShipDateId('all'); // Reset ship date filter when selecting new profile
    dispatch(getCustomerProfileBySmile({ 
      smileName: smileName, 
      includeOrders: true, 
      shop: shopName, 
      page: 1,
      shipDateId: 'all'
    }));
    setSmileNameState(smileName)
  })

  const handleSuccessUpdate = () => {
    fetchProfileWithOrders(ordersPage);
  }

  const handleOrdersPageChange = (page) => {
    setOrdersPage(page);
    fetchProfileWithOrders(page);
  }

  const handleShipDateChange = (shipDateId) => {
    setSelectedShipDateId(shipDateId);
    setOrdersPage(1); // Reset to first page when filtering
    fetchProfileWithOrders(1, shipDateId);
  }

  const handleRemoveBlacklist = async () => {
    if (!window.confirm(`ยืนยันลบ ${profile.contact} ออกจาก Blacklist ?`)) return;
    const success = await removeProfileFromBlacklist(profile.id);
    if (success) {
      toast.success(`ลบ ${profile.contact} ออกจาก Blacklist แล้ว`);
      fetchProfileWithOrders(ordersPage);
    } else {
      toast.error('ลบออกจาก Blacklist ไม่สำเร็จ');
    }
  }

  const handleAddBlacklist = async () => {
    if (!window.confirm(`ยืนยันเพิ่ม ${profile.contact} เข้า Blacklist แบบถาวร (Manual) ? ระบบจะไม่ลบออกอัตโนมัติ`)) return;
    const success = await addProfileToBlacklist(profile.id);
    if (success) {
      toast.success(`เพิ่ม ${profile.contact} เข้า Blacklist (Manual) แล้ว`);
      fetchProfileWithOrders(ordersPage);
    } else {
      toast.error('เพิ่มเข้า Blacklist ไม่สำเร็จ');
    }
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const smileName = queryParams.get('smileName');
    const queryShop = queryParams.get('shop');

    // If the link specified a shop (e.g. from the unpaid list), switch to it
    // first. This effect re-runs with the new shopName and then fetches under
    // the correct shop, so the profile isn't loaded under the wrong shop.
    if (queryShop && queryShop !== shopName) {
      dispatch(setShop(queryShop));
      return;
    }

    // Force a fresh load: this is the customer-management page, where edits happen, so it
    // must not serve a stale session cache.
    dispatch(getCustomerProfiles({ shop: shopName, force: true }));

    if (smileName) {
      setSmileNameState(smileName);
      setOrdersPage(1);
      setSelectedShipDateId('all');
      dispatch(getCustomerProfileBySmile({
        smileName,
        includeOrders: true,
        shop: shopName,
        page: 1,
        shipDateId: 'all',
      }));
    }
  }, [shopName, dispatch]);

  return (
    <div>
      <div className='flex justify-between items-center'>
        <div className='flex-grow p-2 flex items-center gap-3'>
          <h2 className='text-5xl'>Customer Profile</h2>
          {profile?.isVip && (
            <span className="inline-flex items-center rounded-full bg-yellow-100 px-3 py-1 text-lg font-semibold text-yellow-800">
              ⭐ VIP
            </span>
          )}
          {profile?.isBlacklisted && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-lg font-semibold text-red-800">
              ❌ Blacklist{profile?.blacklistSource === 'manual' ? ' (Manual)' : ''}
            </span>
          )}
          {profile?.isBlacklisted && isSuperuser && (
            <button
              className="rounded-md bg-red-600 px-3 py-1 text-sm font-semibold text-white hover:bg-red-500"
              onClick={handleRemoveBlacklist}
            >
              ลบออกจาก Blacklist
            </button>
          )}
          {profile && !profile.isBlacklisted && (
            <button
              className="rounded-md bg-gray-700 px-3 py-1 text-sm font-semibold text-white hover:bg-gray-600"
              onClick={handleAddBlacklist}
            >
              เพิ่มเข้า Blacklist
            </button>
          )}
        </div>
        <div className="bg-white shadow-lg rounded-md p-4 mb-4 flex-grow p-2">
          <ProfileCombo 
            columnSpan={6} 
            profiles={profiles} 
            onChange={handleSelectedChange} 
            searchSocialId
          />
        </div>
      </div>
      <div className="mt-2 bg-gray-200 px-6 py-4 overflow-y-auto max-h-full">
        <div className="bg-white shadow-lg rounded-md px-6 py-2 mb-4">
          <ProfileDetail
            profile={profile}
            accumulatedOrderAmount={profile?.accumulatedOrderAmount}
            onAccountChanged={handleSuccessUpdate}
          />
        </div>
      </div>
      {profile && <div className="mt-2 bg-gray-200 px-6 py-4 overflow-y-auto max-h-full">
        <div className="bg-white shadow-lg rounded-md px-6 py-1 mb-4">
          {<ProfileOrders
            orders={profile.Orders}
            handleSuccessUpdate={handleSuccessUpdate}
            accumulatedOrderAmount={profile.accumulatedOrderAmount}
            ordersPagination={profile.totalPages !== undefined ? {
              currentPage: profile.currentPage,
              totalPages: profile.totalPages,
              totalOrders: profile.totalOrders
            } : null}
            onPageChange={handleOrdersPageChange}
            onShipDateChange={handleShipDateChange}
            selectedShipDateId={selectedShipDateId}
          />}
        </div>
      </div>}
      {profile && <CustomerCaseHistory customerId={profile.id} />}
    </div>
  )
}



export default CustomerProfile
  